import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { IAddressBasicData } from '@df/session/api/address-book/address';
import { NOTICES } from '@df/ui/api/notice/notice.const';
import { TranslationNoticeService } from '@df/ui/translation/translation-notice.service';
import isEqual from 'lodash-es/isEqual';
import pick from 'lodash-es/pick';
import { lastValueFrom } from 'rxjs';
import { NDfModuleLoqate } from './df-module-loqate.config';

declare module '@df/core/service/df-event' {
    interface IEventType {
        [NDfModuleLoqate.Events.EmailResultConverted]: DfEventConfig<
            NDfModuleLoqate.Events.EmailResultConverted,
            {
                items: NDfModuleLoqate.EmailValidationItem[];
            },
            void
        >;
    }

    interface IEventType {
        [NDfModuleLoqate.Events.PhoneResultConverted]: DfEventConfig<
            NDfModuleLoqate.Events.PhoneResultConverted,
            {
                items: NDfModuleLoqate.PhoneValidationItem[];
            },
            void
        >;
    }
}

@Injectable({
    providedIn: 'root'
})
export class LoqateService {
    protected emailCache = new Map<string, NDfModuleLoqate.EmailValidationItem[]>();
    protected phoneCache = new Map<string, NDfModuleLoqate.PhoneValidationItem[]>();
    protected addressCache = new Map<string, NDfModuleLoqate.AddressValidationResult>();

    protected app = inject(App);
    protected http = inject(HttpClient);
    protected translationNoticeService = inject(TranslationNoticeService);
    protected options = inject<NDfModuleLoqate.IModuleOptions>(NDfModuleLoqate.OPTIONS);
    protected dfEventService = inject(DfEventService);

    get isEnabled() {
        return this.options.enabled;
    }

    async validateEmail(email: string): Promise<NDfModuleLoqate.EmailValidationItem[] | undefined> {
        if (this.canGetEmailFromCache(email)) {
            return this.emailCache.get(email);
        }

        const params = this.getRequiredEmailParams();

        params.append('Email', email);

        const result = await lastValueFrom(
            this.http.get<NDfModuleLoqate.EmailValidationResponse>(NDfModuleLoqate.Endpoint.Email + '?' + params.toString())
        );

        const items = result.Items;

        this.emailCache.set(email, items);

        return items;
    }

    async validatePhone(phone: string, countryCode = 'GB'): Promise<NDfModuleLoqate.PhoneValidationItem[] | undefined> {
        const cacheKey = `${phone}.${countryCode}`;

        if (this.canGetPhoneFromCache(phone, countryCode)) {
            return this.phoneCache.get(cacheKey);
        }

        const params = this.getRequiredPhoneParams();

        params.append('Phone', phone);
        params.append('Country', countryCode);

        const result = await lastValueFrom(
            this.http.get<NDfModuleLoqate.PhoneValidationResponse>(NDfModuleLoqate.Endpoint.Phone + '?' + params.toString())
        );

        const items = result.Items;

        this.phoneCache.set(cacheKey, items);

        return items;
    }

    async validateAddress(address: IAddressBasicData): Promise<NDfModuleLoqate.AddressValidationResult> {
        const requestAddress = this.toRequestAddress(address);
        const cacheKey = JSON.stringify(requestAddress);

        if (this.addressCache.has(cacheKey) && this.addressCache.get(cacheKey) !== undefined) {
            return this.addressCache.get(cacheKey) as NDfModuleLoqate.AddressValidationResult;
        }

        const request: NDfModuleLoqate.AddressValidationRequest = {
            Key: this.options.addressKey || '',
            Addresses: [requestAddress]
        };

        const privatePart = pick(address, 'firstname', 'lastname', 'telephone', 'telephone2', 'country', 'prefix');
        const result = (await lastValueFrom(this.http.post(NDfModuleLoqate.Endpoint.Address, request))) as any;
        const matches = result[0].Matches;
        const bestMatch = matches[0] as NDfModuleLoqate.MatchedAddress;
        const convertedAddresses = matches.map(i => Object.assign(this.toAddress(i), privatePart));

        const addressValidationResult = {
            matches: convertedAddresses,
            valid: isEqual(this.toRequestAddress(convertedAddresses[0]), requestAddress),
            hasSuggestion: ['A', 'B', 'C'].includes(bestMatch.AQI)
        };

        this.addressCache.set(cacheKey, addressValidationResult);

        return addressValidationResult;
    }

    /**
     * Check if the response is a generic error, do not block the form.
     *
     * `-1`   - Unknown error
     * `2`    - Unknown key
     *
     * @see https://www.NDfModuleLoqate.com/developers/api/generic-errors/
     *
     * @param {NDfModuleLoqate.ValidationItemError} item
     */
    isGenericError(item: NDfModuleLoqate.ValidationItemError): boolean {
        if (item.Error && ['-1', '2'].indexOf(item.Error) !== -1) {
            this.app.logger.info(item);

            return true;
        }

        return false;
    }

    getEmailError(items: NDfModuleLoqate.EmailValidationItem[] | undefined, email: string) {
        if (items) {
            this.dfEventService.dispatch({
                id: NDfModuleLoqate.Events.EmailResultConverted,
                data: {
                    items: items
                }
            });

            if (items.length === 1 && typeof items[0].Error !== 'undefined') {
                // return `null` if there is a generic error
                if (this.isGenericError(items[0])) {
                    return null;
                }

                // Incorrect key, do not block the form
                if (items[0].Error === '1002') {
                    this.app.logger.info(items[0]);

                    return null;
                }

                return {
                    LOQATE_INVALID_EMAIL: { message: items[0].Description }
                };
            } else if (items.length === 1 && typeof items[0].ResponseCode !== 'undefined' && items[0].ResponseCode === 'Invalid') {
                return {
                    LOQATE_INVALID_EMAIL: { message: items[0].ResponseMessage }
                };
            } else if (items.length === 0) {
                // return console info but validation should not block the form
                this.app.logger.info('Sorry, there were no results');

                return null;
            }
        }

        return null;
    }

    getPhoneError(items: NDfModuleLoqate.PhoneValidationItem[], phone: string) {
        this.dfEventService.dispatch({
            id: NDfModuleLoqate.Events.PhoneResultConverted,
            data: {
                items: items
            }
        });

        if (items.length === 1 && typeof items[0].Error !== 'undefined') {
            // return `null` if there is a generic error
            if (this.isGenericError(items[0])) {
                return null;
            }

            NOTICES['LOQATE_INVALID'] = items[0].Description || 'Please enter a valid phone number';

            return {
                LOQATE_INVALID_PHONE: { message: items[0].Description }
            };
        } else if (items.length === 1 && typeof items[0].IsValid !== 'undefined' && items[0].IsValid === 'No') {
            return {
                LOQATE_INVALID_PHONE: { message: 'Please enter a valid phone number' }
            };
        } else if (items.length === 0) {
            // return console info but validation should not block the form
            this.app.logger.info('Sorry, there were no results');

            return null;
        }

        return null;
    }

    protected canGetEmailFromCache(email: string): boolean {
        return this.emailCache.has(email) && this.emailCache.get(email) !== undefined;
    }

    protected canGetPhoneFromCache(phone: string, countryCode: string): boolean {
        const cacheKey = `${phone}.${countryCode}`;

        return this.phoneCache.has(cacheKey) && this.phoneCache.get(cacheKey) !== undefined;
    }

    protected getRequiredEmailParams(): URLSearchParams {
        return new URLSearchParams({
            Key: this.options.emailKey || '',
            Timeout: this.options.timeout.toString()
        });
    }

    protected getRequiredPhoneParams(): URLSearchParams {
        return new URLSearchParams({
            Key: this.options.phoneKey || ''
        });
    }

    protected toRequestAddress(address: IAddressBasicData): NDfModuleLoqate.Address {
        return {
            Country: address.country_id || '',
            AdministrativeArea: address.region || '',
            Locality: address.city || '',
            Address1: address.street_1 || '',
            Address2: address.street_2 || '',
            PostalCode: address.postcode || ''
        };
    }

    protected toAddress(data: NDfModuleLoqate.MatchedAddress): IAddressBasicData {
        return {
            city: data.Locality,
            postcode: data.PostalCode,
            street_1: data.DeliveryAddress1,
            street_2: data.DeliveryAddress2,
            country_id: data.Country,
            region: data.AdministrativeArea
        };
    }
}
